import { supabase } from '../lib/supabase';
import { urgentSMSService } from './urgentSMSService';
import { pushNotificationService } from './pushNotificationService';

export const realtimeNotificationService = {
  // Active subscriptions registry
  activeSubscriptions: new Map(),

  /**
   * Subscribe to compliance alerts real-time updates
   */
  subscribeToComplianceAlerts(companyId, callback) {
    const channel = supabase
      ?.channel(`compliance-alerts-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_alerts',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('Compliance alert change:', payload);
          
          // Transform payload to camelCase
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformComplianceAlert(payload?.new) : null,
            old: payload?.old ? this.transformComplianceAlert(payload?.old) : null
          };

          callback(transformedPayload);

          // Send urgent SMS for critical unresolved alerts
          if (payload?.eventType === 'INSERT' && payload?.new) {
            const alert = payload?.new;
            if ((alert?.severity === 'critical' || alert?.severity === 'high') && !alert?.is_resolved) {
              this.sendUrgentNotification(alert, 'compliance');
            }
          }
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`compliance-alerts-${companyId}`, channel);
    return channel;
  },

  /**
   * Subscribe to environmental alerts real-time updates
   */
  subscribeToEnvironmentalAlerts(userId, callback) {
    const channel = supabase
      ?.channel(`environmental-alerts-${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'environmental_alerts',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Environmental alert change:', payload);
          
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformEnvironmentalAlert(payload?.new) : null,
            old: payload?.old ? this.transformEnvironmentalAlert(payload?.old) : null
          };

          callback(transformedPayload);

          // Send urgent SMS for critical unacknowledged alerts
          if (payload?.eventType === 'INSERT' && payload?.new) {
            const alert = payload?.new;
            if ((alert?.severity === 'critical' || alert?.severity === 'high') && !alert?.is_acknowledged) {
              this.sendUrgentNotification(alert, 'environmental');
            }
          }
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`environmental-alerts-${userId}`, channel);
    return channel;
  },

  /**
   * Subscribe to notification queue real-time updates
   */
  subscribeToNotificationQueue(userId, callback) {
    const channel = supabase
      ?.channel(`notification-queue-${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_queue',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Notification queue change:', payload);
          
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformNotification(payload?.new) : null,
            old: payload?.old ? this.transformNotification(payload?.old) : null
          };

          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`notification-queue-${userId}`, channel);
    return channel;
  },

  /**
   * Subscribe to escalation history real-time updates
   */
  subscribeToEscalationHistory(companyId, callback) {
    const channel = supabase
      ?.channel(`escalation-history-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'escalation_history',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('Escalation history change:', payload);
          
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformEscalation(payload?.new) : null
          };

          callback(transformedPayload);

          // Send urgent notification for new escalations
          if (payload?.new) {
            this.sendEscalationNotification(payload?.new);
          }
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`escalation-history-${companyId}`, channel);
    return channel;
  },

  /**
   * Subscribe to alert response tracking real-time updates
   */
  subscribeToAlertResponseTracking(companyId, callback) {
    const channel = supabase
      ?.channel(`alert-response-tracking-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'alert_response_tracking',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('Alert response tracking change:', payload);
          
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformAlertTracking(payload?.new) : null,
            old: payload?.old ? this.transformAlertTracking(payload?.old) : null
          };

          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`alert-response-tracking-${companyId}`, channel);
    return channel;
  },

  /**
   * Subscribe to audit logs real-time updates
   */
  subscribeToAuditLogs(companyId, callback) {
    const channel = supabase
      ?.channel(`audit-logs-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'application_logs',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('Audit log change:', payload);
          
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformAuditLog(payload?.new) : null
          };

          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`audit-logs-${companyId}`, channel);
    return channel;
  },

  /**
   * Unsubscribe from a specific channel
   */
  unsubscribe(channelKey) {
    const channel = this.activeSubscriptions?.get(channelKey);
    if (channel) {
      supabase?.removeChannel(channel);
      this.activeSubscriptions?.delete(channelKey);
      console.log(`Unsubscribed from ${channelKey}`);
    }
  },

  /**
   * Unsubscribe from all active channels
   */
  unsubscribeAll() {
    this.activeSubscriptions?.forEach((channel, key) => {
      supabase?.removeChannel(channel);
      console.log(`Unsubscribed from ${key}`);
    });
    this.activeSubscriptions?.clear();
  },

  /**
   * Send urgent notification via SMS and email
   */
  async sendUrgentNotification(alert, alertType) {
    try {
      // Get user notification preferences
      const { data: preferences } = await supabase
        ?.from('user_notification_preferences')
        ?.select('*')
        ?.eq('user_id', alert?.user_id || alert?.created_by)
        ?.single();

      const userId = alert?.user_id || alert?.created_by;
      if (!userId) return;

      // Prepare notification data
      const notificationData = {
        userIds: [userId],
        type: alertType === 'compliance' ? 'compliance_alert' : 'environmental_alert',
        title: alertType === 'compliance' ? alert?.title : alert?.alert_type?.replace(/_/g, ' '),
        body: alert?.message || alert?.description || 'Urgent alert requires your attention',
        priority: alert?.severity === 'critical' ? 'urgent' : 'high',
        requiresConfirmation: true,
        data: {
          alertId: alert?.id,
          alertType,
          severity: alert?.severity,
          location: alert?.location
        }
      };

      // Send push notification
      await pushNotificationService?.sendNotification(notificationData);

      // Send SMS if enabled
      if (preferences?.urgent_alert_sms && preferences?.phone_number) {
        await urgentSMSService?.sendUrgentAlert({
          phoneNumber: preferences?.phone_number,
          message: `${notificationData?.title}: ${notificationData?.body}`,
          alertId: alert?.id,
          severity: alert?.severity
        });
      }

      // Send email if enabled
      if (preferences?.urgent_alert_email) {
        await this.sendUrgentEmail(userId, notificationData);
      }
    } catch (error) {
      console.error('Error sending urgent notification:', error);
    }
  },

  /**
   * Send escalation notification
   */
  async sendEscalationNotification(escalation) {
    try {
      if (!escalation?.escalated_to) return;

      // Send push notification to escalated user
      await pushNotificationService?.sendTemplatedNotification(
        'Escalation Alert',
        [escalation?.escalated_to],
        {
          original_assignee: escalation?.original_assignee || 'Unknown',
          alert_title: 'Alert Escalation'
        }
      );

      // Get preferences for escalated user
      const { data: preferences } = await supabase
        ?.from('user_notification_preferences')
        ?.select('*')
        ?.eq('user_id', escalation?.escalated_to)
        ?.single();

      // Send SMS if enabled
      if (preferences?.urgent_alert_sms && preferences?.phone_number) {
        await urgentSMSService?.sendUrgentAlert({
          phoneNumber: preferences?.phone_number,
          message: `ESCALATION: Alert has been escalated to you. Reason: ${escalation?.escalation_reason}`,
          alertId: escalation?.alert_response_tracking_id,
          severity: 'critical'
        });
      }
    } catch (error) {
      console.error('Error sending escalation notification:', error);
    }
  },

  // Transform functions (snake_case to camelCase)
  transformComplianceAlert(alert) {
    return {
      id: alert?.id,
      companyId: alert?.company_id,
      createdBy: alert?.created_by,
      alertType: alert?.alert_type,
      severity: alert?.severity,
      title: alert?.title,
      message: alert?.message,
      dueDate: alert?.due_date,
      isResolved: alert?.is_resolved,
      resolvedBy: alert?.resolved_by,
      resolvedAt: alert?.resolved_at,
      metadata: alert?.metadata,
      createdAt: alert?.created_at,
      updatedAt: alert?.updated_at
    };
  },

  transformEnvironmentalAlert(alert) {
    return {
      id: alert?.id,
      deviceId: alert?.device_id,
      userId: alert?.user_id,
      alertType: alert?.alert_type,
      sensorType: alert?.sensor_type,
      thresholdValue: alert?.threshold_value,
      actualValue: alert?.actual_value,
      severity: alert?.severity,
      message: alert?.message,
      isAcknowledged: alert?.is_acknowledged,
      acknowledgedAt: alert?.acknowledged_at,
      acknowledgedBy: alert?.acknowledged_by,
      resolvedAt: alert?.resolved_at,
      createdAt: alert?.created_at
    };
  },

  transformNotification(notification) {
    return {
      id: notification?.id,
      userId: notification?.user_id,
      companyId: notification?.company_id,
      taskId: notification?.task_id,
      notificationType: notification?.notification_type,
      priority: notification?.priority,
      title: notification?.title,
      message: notification?.message,
      phoneNumber: notification?.phone_number,
      status: notification?.status,
      scheduledFor: notification?.scheduled_for,
      sentAt: notification?.sent_at,
      deliveredAt: notification?.delivered_at,
      readAt: notification?.read_at,
      isUrgent: notification?.is_urgent,
      metadata: notification?.metadata,
      createdAt: notification?.created_at
    };
  },

  transformEscalation(escalation) {
    return {
      id: escalation?.id,
      alertResponseTrackingId: escalation?.alert_response_tracking_id,
      escalationRuleId: escalation?.escalation_rule_id,
      originalAssignee: escalation?.original_assignee,
      originalAssignedAt: escalation?.original_assigned_at,
      escalatedTo: escalation?.escalated_to,
      escalatedAt: escalation?.escalated_at,
      escalationReason: escalation?.escalation_reason,
      escalationActionsTaken: escalation?.escalation_actions_taken,
      timeoutMinutes: escalation?.timeout_minutes,
      actualResponseTimeMinutes: escalation?.actual_response_time_minutes,
      escalationStatus: escalation?.escalation_status,
      resolvedAt: escalation?.resolved_at,
      resolutionNotes: escalation?.resolution_notes,
      smsSent: escalation?.sms_sent,
      smsSentAt: escalation?.sms_sent_at,
      emailSent: escalation?.email_sent,
      emailSentAt: escalation?.email_sent_at,
      companyId: escalation?.company_id,
      metadata: escalation?.metadata,
      createdAt: escalation?.created_at
    };
  },

  transformAlertTracking(tracking) {
    return {
      id: tracking?.id,
      alertId: tracking?.alert_id,
      alertType: tracking?.alert_type,
      alertSeverity: tracking?.alert_severity,
      assignedTo: tracking?.assigned_to,
      assignedAt: tracking?.assigned_at,
      assignedBy: tracking?.assigned_by,
      firstViewedAt: tracking?.first_viewed_at,
      acknowledgedAt: tracking?.acknowledged_at,
      resolvedAt: tracking?.resolved_at,
      responseTimeMinutes: tracking?.response_time_minutes,
      escalationRuleId: tracking?.escalation_rule_id,
      isEscalated: tracking?.is_escalated,
      escalatedAt: tracking?.escalated_at,
      escalationReason: tracking?.escalation_reason,
      companyId: tracking?.company_id,
      metadata: tracking?.metadata,
      createdAt: tracking?.created_at,
      updatedAt: tracking?.updated_at
    };
  },

  transformAuditLog(log) {
    return {
      id: log?.id,
      companyId: log?.company_id,
      userId: log?.user_id,
      level: log?.level,
      category: log?.category,
      action: log?.action,
      description: log?.description,
      resourceType: log?.resource_type,
      resourceId: log?.resource_id,
      metadata: log?.metadata,
      ipAddress: log?.ip_address,
      userAgent: log?.user_agent,
      createdAt: log?.created_at
    };
  }
};
